export const blogPosts = [
  {
    slug: 'stop-starting-over-every-monday',
    title: 'Stop starting over every Monday',
    description:
      'Why reset culture keeps you anxious and stuck — and how small daily systems beat dramatic restarts.',
    date: '2026-06-12',
    readTime: '6 min',
    category: 'Productivity',
    body: [
      {
        type: 'p',
        text: 'There is a familiar Monday fantasy: new planner, new rules, new you. By Wednesday the fantasy is gone, and by Friday you are quietly planning next Monday’s restart. That loop is not a motivation problem. It is a design problem.',
      },
      {
        type: 'h2',
        text: 'Dramatic resets create dramatic guilt',
      },
      {
        type: 'p',
        text: 'A hard reset feels productive because it is visible. You delete apps, rewrite goals, announce a streak. But the bar is so high that one imperfect day collapses the whole identity. Anxiety loves that collapse — it turns a normal Tuesday into proof you “always fail.”',
      },
      {
        type: 'p',
        text: 'High performers do not restart every week. They adjust. They keep a thin thread of continuity so progress survives messy days.',
      },
      {
        type: 'h2',
        text: 'Build a floor, not a ceiling',
      },
      {
        type: 'p',
        text: 'A ceiling goal says: meditate 40 minutes, write 2,000 words, hit every target. A floor goal says: open the journal for two minutes, name one mood, take one next step. Floors are boring. Floors also compound.',
      },
      {
        type: 'p',
        text: 'When life gets loud — deadlines, conflict, poor sleep — the floor is what keeps your identity intact. You did not “fall off.” You practiced the minimum. Momentum stays warm.',
      },
      {
        type: 'h2',
        text: 'Use mood as data, not a verdict',
      },
      {
        type: 'p',
        text: 'Anxiety often shows up as urgency to fix everything tonight. Mood tracking slows that impulse. If you notice “tight chest + racing thoughts” after three late nights of overwork, you have a pattern — not a character flaw.',
      },
      {
        type: 'p',
        text: 'Write the pattern down. Ask one calm question: what would protect tomorrow’s floor? Then do that smaller thing. Clarity beats reinvention.',
      },
      {
        type: 'h2',
        text: 'A better Monday',
      },
      {
        type: 'p',
        text: 'Keep last week’s system. Change one constraint. Review one goal. Log how you feel. Talk through the sticky part with a coach that asks questions instead of handing you a new personality.',
      },
      {
        type: 'p',
        text: 'You do not need another Monday. You need continuity that survives real life.',
      },
    ],
  },
  {
    slug: 'anxiety-is-a-signal-not-a-sentence',
    title: 'Anxiety is a signal, not a sentence',
    description:
      'How to treat anxious energy as useful information for focus and decisions — without letting it run the day.',
    date: '2026-05-28',
    readTime: '7 min',
    category: 'Mental performance',
    body: [
      {
        type: 'p',
        text: 'Anxiety often arrives with a story: something is wrong, you are behind, you must act now. Sometimes the body is right that something needs attention. Often the story is too loud for the size of the problem. The skill is separating signal from sentence.',
      },
      {
        type: 'h2',
        text: 'Name it before you obey it',
      },
      {
        type: 'p',
        text: 'When your chest tightens or your thoughts start spinning, write one sentence: “I feel anxious about ___.” Specificity shrinks the fog. “Anxious about the presentation slide deck” is workable. “Anxious about my whole life” is not.',
      },
      {
        type: 'p',
        text: 'This is not positive thinking. It is triage. You cannot steer what you refuse to name.',
      },
      {
        type: 'h2',
        text: 'Ask what the anxiety wants protected',
      },
      {
        type: 'p',
        text: 'Most anxious loops are trying to protect something: reputation, relationships, money, time, belonging. If you can name the protected value, you can choose a proportional response.',
      },
      {
        type: 'p',
        text: 'Protecting a deadline might mean a 25-minute focus block. Protecting a friendship might mean one honest message. Protecting sleep might mean closing the laptop. The body settles when it sees a concrete next move.',
      },
      {
        type: 'h2',
        text: 'Track moods so patterns become obvious',
      },
      {
        type: 'p',
        text: 'One anxious day feels random. Fourteen logged days reveal a map: worse after caffeine-and-inbox mornings, sharper before unscheduled afternoons, calmer after a short walk and a written plan.',
      },
      {
        type: 'p',
        text: 'Patterns turn anxiety from a personal verdict into environmental design. You change the inputs. You do not fight yourself all day.',
      },
      {
        type: 'h2',
        text: 'Keep goals attached to the present week',
      },
      {
        type: 'p',
        text: 'Distant goals feed anxiety when they stay abstract. Bring them close: what does “reach this goal” ask of Thursday? One deliverable. One conversation. One review. Progress is a sequence of honest hours, not a personality upgrade.',
      },
      {
        type: 'p',
        text: 'Signal heard. Sentence declined. Next step chosen.',
      },
    ],
  },
  {
    slug: 'the-two-minute-journal-that-actually-sticks',
    title: 'The two-minute journal that actually sticks',
    description:
      'A minimal journaling practice for busy people who want clarity without another hour-long ritual.',
    date: '2026-05-08',
    readTime: '5 min',
    category: 'Productivity',
    body: [
      {
        type: 'p',
        text: 'Most journaling advice fails busy people because it asks for a ceremony. Blank page. Soft lighting. Forty free minutes. Real life rarely offers that — and waiting for perfect conditions is how the habit dies.',
      },
      {
        type: 'h2',
        text: 'Two minutes, three lines',
      },
      {
        type: 'p',
        text: 'Keep it tiny enough that resistance cannot argue:',
      },
      {
        type: 'ul',
        items: [
          'What is on my mind right now?',
          'How do I feel, in one word?',
          'What is one useful next step?',
        ],
      },
      {
        type: 'p',
        text: 'That is it. No essay required. If you have more to say, write more. If you do not, stop. Consistency beats depth on day one.',
      },
      {
        type: 'h2',
        text: 'Why this works for focus',
      },
      {
        type: 'p',
        text: 'Your working memory is small. Unwritten worries occupy the same space as the work you meant to do. Dumping the worry onto a page frees attention. Naming the mood prevents you from mistaking fatigue for failure. Choosing one next step ends the spiral of infinite options.',
      },
      {
        type: 'h2',
        text: 'Pair it with goals you can see',
      },
      {
        type: 'p',
        text: 'Once a week, skim your entries and ask: what keeps repeating? That repetition is often your real goal in disguise — better sleep, cleaner priorities, less people-pleasing, a project you keep avoiding.',
      },
      {
        type: 'p',
        text: 'Turn the theme into a goal with a finish line you can feel this month. Then let the daily two minutes report whether you are moving.',
      },
      {
        type: 'h2',
        text: 'Let AI coach the sticky parts',
      },
      {
        type: 'p',
        text: 'Some entries are clear. Others loop. That is when a calm coach helps: not to diagnose you, but to ask better questions until your own next step becomes obvious.',
      },
      {
        type: 'p',
        text: 'Two minutes today beats a perfect journal you never open.',
      },
    ],
  },
  {
    slug: 'deep-work-without-burning-out',
    title: 'Deep work without burning out',
    description:
      'Protect focus like an athlete protects recovery — intensity, recovery, and honest mood checks.',
    date: '2026-04-22',
    readTime: '8 min',
    category: 'Productivity',
    body: [
      {
        type: 'p',
        text: 'Deep work is not a personality trait. It is a cycle: focused effort, recovery, review. People who grind without recovery do not get more done over a month — they get brittle. Anxiety rises. Quality falls. Then they call it a motivation crisis.',
      },
      {
        type: 'h2',
        text: 'Schedule intensity on purpose',
      },
      {
        type: 'p',
        text: 'Pick one deep block most weekdays. Ninety minutes is plenty. Phone elsewhere. One outcome only. Write the outcome before you start so your brain knows what “done” looks like.',
      },
      {
        type: 'p',
        text: 'If anxiety spikes mid-block, pause for sixty seconds and jot what the fear is about. Then return to the smallest unfinished piece. You are training attention, not proving toughness.',
      },
      {
        type: 'h2',
        text: 'Recovery is part of the work',
      },
      {
        type: 'p',
        text: 'After intensity, take a real break: walk, water, no new inputs. Mood tracking helps here. If your end-of-day notes say “wired and empty” three times a week, you are overdrawing the account.',
      },
      {
        type: 'p',
        text: 'Athletes do not apologize for recovery. Knowledge work should not either.',
      },
      {
        type: 'h2',
        text: 'Goals that respect energy',
      },
      {
        type: 'p',
        text: 'A goal that ignores sleep, stress, and calendar reality is a fantasy. Rewrite goals in terms of weekly capacity: three deep sessions, one review, one relationship check-in. Ambition stays. Self-cruelty goes.',
      },
      {
        type: 'h2',
        text: 'Review without the courtroom',
      },
      {
        type: 'p',
        text: 'Once a week, look at moods, journal themes, and goal progress together. Ask: what helped focus? What drained it? What will I protect next week?',
      },
      {
        type: 'p',
        text: 'That is sustainable high performance — calm enough to think, structured enough to finish.',
      },
    ],
  },
  {
    slug: 'make-goals-you-will-still-want-in-30-days',
    title: 'Make goals you will still want in 30 days',
    description:
      'How to set goals that survive mood swings, busy weeks, and the mid-month motivation drop.',
    date: '2026-04-03',
    readTime: '6 min',
    category: 'Goals',
    body: [
      {
        type: 'p',
        text: 'Most abandoned goals were never bad ideas. They were poorly timed, vaguely worded, or attached to a mood that did not last. Motivation is weather. Goals need climate.',
      },
      {
        type: 'h2',
        text: 'Start from a lived friction',
      },
      {
        type: 'p',
        text: 'Open your journal. Find what keeps annoying you: late nights, avoided conversations, half-finished projects, anxiety before mornings. Good goals grow from friction you already feel — not from a stranger’s morning routine.',
      },
      {
        type: 'h2',
        text: 'Define the finish so a stranger would recognize it',
      },
      {
        type: 'p',
        text: '“Be more productive” is not a goal. “Ship the proposal draft by the 20th and review it once with a clear yes/no” is a goal. If you cannot describe done, your brain will never feel finished — and unfinished goals feed anxiety.',
      },
      {
        type: 'h2',
        text: 'Attach a weekly heartbeat',
      },
      {
        type: 'p',
        text: 'Every goal needs a recurring check: fifteen minutes on Sunday, or a midweek mood-and-progress note. The heartbeat is more important than the original burst of enthusiasm.',
      },
      {
        type: 'p',
        text: 'On low-energy days, the heartbeat might only answer: am I still pointed at this? If yes, keep the floor. If no, rewrite the goal honestly instead of ghosting it.',
      },
      {
        type: 'h2',
        text: 'Let coaching sharpen the next step',
      },
      {
        type: 'p',
        text: 'When you stall, do not invent a new life plan. Ask better questions: what is the smallest action that would make tomorrow easier? What am I avoiding? What would “good enough” look like this week?',
      },
      {
        type: 'p',
        text: 'Goals you still want in 30 days are usually quieter, clearer, and kinder — and they still move your life forward.',
      },
    ],
  },
];

export function getPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug) || null;
}
